/* eslint-disable @typescript-eslint/no-unused-vars */
import { inject, Injectable } from '@angular/core';
import { NbAuthJWTToken, NbAuthResult, NbAuthStrategyClass, NbAuthStrategyOptions } from '@nebular/auth';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import {
  browserLocalPersistence,
  browserSessionPersistence,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getIdToken,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updatePassword,
  User,
} from '@angular/fire/auth';
import { firebasePasswordStrategyOptions, NbFirebasePasswordStrategyOptions } from '@nebular/firebase-auth';
import { UserStore } from '../store/user/user.store';
import { NbFirebaseBaseStrategy } from './firebase-base.strategy';

@Injectable({ providedIn: 'root' })
export class NbFirebasePasswordStrategy extends NbFirebaseBaseStrategy {
  protected override defaultOptions: NbFirebasePasswordStrategyOptions = firebasePasswordStrategyOptions;
  private refreshBehavior: BehaviorSubject<NbAuthResult | null> = new BehaviorSubject<NbAuthResult | null>(null);
  private authStore = inject(UserStore, { optional: true });
  constructor() {
    super();
    onAuthStateChanged(this.afAuth, (user) => {
      const module = 'refreshToken';
      if (user) {
        user
          .getIdTokenResult(true)
          .then((x) => {
            this.refreshBehavior.next(this.newDefAuthRes(module, true, { token: x.token }));
          })
          .catch((x) =>
            this.refreshBehavior.next(
              this.newDefAuthRes(module, false, {
                errors: [(x as Error).message],
                redirectIsSuccess: false,
              }),
            ),
          );
        this.authStore?.setUsers(user);
      } else {
        this.refreshBehavior.next(
          this.newDefAuthRes(module, false, {
            errors: ['no/empty user'],
            redirectIsSuccess: false,
          }),
        );
      }
    });
  }

  static setup(options: NbFirebasePasswordStrategyOptions): [NbAuthStrategyClass, NbAuthStrategyOptions] {
    return [NbFirebasePasswordStrategy, options];
  }

  authenticate(data: { email: string; password: string; rememberMe: boolean }): Observable<NbAuthResult> {
    const module = 'login';

    return from(signInWithEmailAndPassword(this.afAuth, data.email, data.password)).pipe(
      switchMap((res) =>
        from(this.afAuth.setPersistence(data.rememberMe ? browserLocalPersistence : browserSessionPersistence)).pipe(
          map(() => res),
        ),
      ),
      switchMap((res) => this.processSuccess(res, module)),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  override refreshToken(_userAuthState?: NbAuthJWTToken | User | null): Observable<NbAuthResult> {
    return this.refreshBehavior.pipe(
      // tap((x) => console.log('behavior-token', x)),
      filter((x) => Boolean(x)),
    ) as Observable<NbAuthResult>;
  }

  override register({ email, password }: { email: string; password: string }): Observable<NbAuthResult> {
    const module = 'register';
    return from(createUserWithEmailAndPassword(this.afAuth, email, password)).pipe(
      switchMap((res) => this.processSuccess(res, module)),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  override requestPassword({ email }: { email: string }): Observable<NbAuthResult> {
    const module = 'requestPassword';
    return from(sendPasswordResetEmail(this.afAuth, email)).pipe(
      map(() => this.newDefAuthRes(module, true)),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  override resetPassword({ code, password }: { code: string; password: string }): Observable<NbAuthResult> {
    const module = 'resetPassword';
    return from(confirmPasswordReset(this.afAuth, code, password)).pipe(
      map(() => this.newDefAuthRes(module, true)),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  protected updatePassword(user: User, newPassword: string, module: string) {
    return from(updatePassword(user, newPassword)).pipe(
      map(() => this.newDefAuthRes(module, true, { redirectIsSuccess: false })),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  protected refreshIdToken(user: User, module: string): Observable<NbAuthResult> {
    return from(getIdToken(user, true)).pipe(
      map((token) => this.newDefAuthRes(module, true, { token })),
      catchError((error) => this.processFailure(error, module)),
    );
  }
}
